import Box from '@totallymoney/ui/components/Box'
import Grid from '@totallymoney/ui/components/Grid'
import Heading from '@totallymoney/ui/components/Heading'
import LinkWithChevron from '@totallymoney/ui/components/LinkWithChevron'
import Stack from '@totallymoney/ui/components/Stack'
import Text from '@totallymoney/ui/components/Text'
import theme from '@totallymoney/ui/theme'

import { getAppConfig } from '@utils/getAppConfig'
import { getIsProductPortraitImage } from '@utils/getIsProductPortraitImage'

import {
    CreditCardAlternativeProduct,
    CreditCardAlternativeProductsCategory,
} from '@appTypes/EligibilityApi.interface'

import useAppQueryState from '@hooks/useAppQueryState'
import useLogEvents from '@hooks/useLogEvents'
import useSetState from '@hooks/useSetState'

import useGetAlternativeProductApplicationUrl from '@routes/AlternativeProductsTableRoute/hooks/useGetAlternativeProductApplicationUrl'
import HighlightHeroIneligible from '@routes/ScoredTableRoute/components/ScoredTable/components/HighlightHero/components/HighlightHeroIneligible'

import EligibilityFactorsCard from '@components/EligibilityFactorsCard'

import { KBA_SCENARIO_KEYS } from '@config/kbaScenarioKeys'

import AlternativeProductsTableHeader from './components/AlternativeProductsTableHeader'
import AlternativeProductsTableItem from './components/AlternativeProductsTableItem'
import getCategoryByAlternativeCategoryId from './utils/getCategoryByAlternativeProductCategoryId'

interface AlternativeProductsTableProps {
    alternativeProductCategories: CreditCardAlternativeProductsCategory[]
    numberOfRedEligibilityFactors?: number
}

interface AlternativeProductsTableState {
    selectedCategory: string
}

const AlternativeProductsTable = ({
    alternativeProductCategories,
    numberOfRedEligibilityFactors = 0,
}: AlternativeProductsTableProps) => {
    const appConfig = getAppConfig()
    const { appQueryState } = useAppQueryState()
    const getAlternativeProductApplicationUrl =
        useGetAlternativeProductApplicationUrl()
    const logEvents = useLogEvents()

    const [alternativeProductsState, setAlternativeProductsState] =
        useSetState<AlternativeProductsTableState>({
            selectedCategory: alternativeProductCategories[0].id,
        })

    const onCategoryChange = (category: string) => {
        logEvents.logAlternativeProductCategoryChangeEvent({
            categoryName: category,
        })

        setAlternativeProductsState({
            selectedCategory: category,
        })
    }

    const activeCategory = getCategoryByAlternativeCategoryId(
        alternativeProductCategories,
        alternativeProductsState.selectedCategory
    )

    const onApplyPress = ({
        alternativeProduct,
        productPosition,
        applicationUrl,
    }: {
        alternativeProduct: CreditCardAlternativeProduct
        productPosition: number
        applicationUrl: string
    }) => {
        logEvents.logAlternativeProductApplyEvent({
            productCode: alternativeProduct.productCode,
            clickSource: appConfig.CLICK_SOURCE_WHITELABEL,
            rowNumber: productPosition,
            trigger: 'row',
        })

        window.open(applicationUrl, '_blank')
    }

    const isOnBoardingRequired =
        appQueryState.kbaScenario ===
            KBA_SCENARIO_KEYS.ONBOARDING_REQUIRED_HD_FOUND ||
        appQueryState.kbaScenario ===
            KBA_SCENARIO_KEYS.ONBOARDING_REQUIRED_HD_NOT_FOUND

    const showHighlightedCreditCardHero =
        appQueryState.scoredCardUrl != null && appQueryState.isCardSpecific

    const showEligibilityFactorsCard = numberOfRedEligibilityFactors >= 1

    return (
        <Box bg="#faf8f8" pb="space60">
            <Grid>
                <Box mt="space30" mb="space10" gridColumn={'1/13'}>
                    <LinkWithChevron
                        href={`${appConfig.ACCOUNT_URL}offers?customerId=${appQueryState.customerId}`}
                        variant="100"
                        direction="left"
                    >
                        Back to offers
                    </LinkWithChevron>
                </Box>
            </Grid>
            <Grid>
                <Box gridColumn={'1/13'} pb="space30">
                    <Stack space="space30">
                        <Stack space="space10">
                            <Heading variant="100">Your card results</Heading>
                            <Text variant="120">
                                We couldn’t find any cards you’re eligible for
                                right now, but we’ve got some other options for
                                you.
                            </Text>
                        </Stack>

                        {showHighlightedCreditCardHero ? (
                            <HighlightHeroIneligible
                                item={null}
                                totalCreditCards={0}
                            />
                        ) : null}

                        {showEligibilityFactorsCard ? (
                            <EligibilityFactorsCard
                                route="alternativeProducts"
                                isOnBoardingRequired={isOnBoardingRequired}
                                numberOfRedEligibilityFactors={
                                    numberOfRedEligibilityFactors
                                }
                            />
                        ) : null}
                    </Stack>
                </Box>
            </Grid>
            <AlternativeProductsTableHeader
                categories={alternativeProductCategories}
                initialCategory={alternativeProductCategories[0].id}
                onCategoryChange={onCategoryChange}
            />
            <Box pb="space50" />
            <Grid>
                <Box gridColumn={'1/13'} mb="space50">
                    <Box mb="space20">
                        <Heading variant="60">
                            {activeCategory?.displayName}
                        </Heading>
                    </Box>
                    <Text variant="120">{activeCategory?.description}</Text>
                </Box>
            </Grid>
            <Grid style={{ gridGap: theme.spacingM }}>
                {activeCategory?.alternativeProducts.map(
                    (alternativeProduct, index) => {
                        const applyUrl = getAlternativeProductApplicationUrl(
                            alternativeProduct.productCode
                        )

                        return (
                            <Box
                                gridColumn={'1/13'}
                                key={alternativeProduct.productCode}
                            >
                                <AlternativeProductsTableItem
                                    item={alternativeProduct}
                                    applyHref={applyUrl}
                                    isPortraitImage={getIsProductPortraitImage(
                                        alternativeProduct.productCode
                                    )}
                                    onApplyPress={() => {
                                        onApplyPress({
                                            alternativeProduct,
                                            applicationUrl: applyUrl,
                                            productPosition: index + 1,
                                        })
                                    }}
                                />
                            </Box>
                        )
                    }
                )}
            </Grid>
        </Box>
    )
}

export default AlternativeProductsTable
